import React from 'react'

import Badge from '@instabox/internal-web-ui/components/Badge'
import Button from '@instabox/internal-web-ui/components/Button'
import Table from '@instabox/internal-web-ui/components/Table'
import CheckIcon from '@instabox/internal-web-ui/icons/CheckIcon'
import PlusIcon from '@instabox/internal-web-ui/icons/PlusIcon'
import { BaselinePackage } from '@instabox/lockers-update-common/types'
import { orderBy, get } from 'lodash'

import Dropdown from '../components/Dropdown'
import PackageDetailsButton from '../components/PackageDetailsButton'
import PackageVersionDetailsButton from '../components/PackageVersionDetailsButton'
import TableActionsContainer from '../components/TableActionsContainer'

export interface PackageVersion {
  name: string
  version: string
}

type OnVersionChangeAction = (packageName: string, newVersion: string) => void
type OnTogglePackageAction = (pkg: BaselinePackage) => void

interface BaselinePackageTable {
  isLoading: boolean
  packages: BaselinePackage[]
  // Determines wether this table should list the packages that are in the baseline
  // or the ones that are not.
  inBaseline?: boolean
  onTogglePackage: OnTogglePackageAction
  onVersionChange?: OnVersionChangeAction
}

const BaselinePackagesTable: React.FC<BaselinePackageTable> = ({
  isLoading,
  packages,
  inBaseline = false,
  onTogglePackage,
  onVersionChange = () => null,
}) => {
  function handlePackageVersionChanged(event: React.ChangeEvent<HTMLSelectElement>, name: string): void {
    event.preventDefault()
    const version = event.target.value
    onVersionChange(name, version)
  }

  function getActionButton(pkg: BaselinePackage) {
    return (
      <Button
        size="sm"
        shape="outlined"
        icon={inBaseline ? <CheckIcon /> : <PlusIcon />}
        variant={inBaseline ? 'success' : 'primary'}
        onClick={() => onTogglePackage(pkg)}
      />
    )
  }

  function renderVersionCol(pkg: BaselinePackage) {
    const versions = pkg.versions ?? []

    if (inBaseline) {
      const options: string[] = orderBy(versions, (v) => v.createdAt, 'desc').map((version) => version.version)

      return (
        <Dropdown
          data-testid={`versionDropDown-${pkg.name}`}
          options={options}
          value={pkg.selectedVersion}
          onChange={(e) => handlePackageVersionChanged(e, pkg.name)}
        />
      )
    }

    return (
      <>
        <Badge variant="info" size="sm">
          Available: {versions.length}
        </Badge>
        <Badge variant="teal" size="sm" style={{ marginLeft: '10px' }}>
          Latest: {get(versions, '[0].version', 'N/A')}
        </Badge>
      </>
    )
  }

  function renderNewVersionCol(pkg: BaselinePackage) {
    const versions = pkg.versions ?? []
    const latestVersion = get(versions, '[0].version', 'N/A')
    if (inBaseline && pkg.selectedVersion !== latestVersion) {
      return (
        <Badge variant="warning" size="sm">
          New version available
        </Badge>
      )
    }
    return <></>
  }

  return (
    <div>
      <div className="pb-4">
        <Table
          style={{ overflow: 'hidden' }}
          isLoading={isLoading}
          headers={{
            toggle: '',
            name: 'Package',
            newVersion: '',
            version: inBaseline ? 'Version' : 'Available versions',
            details: '',
          }}
          rows={packages.map((pkg) => ({
            toggle: getActionButton(pkg),
            name: pkg.name,
            newVersion: renderNewVersionCol(pkg),
            version: renderVersionCol(pkg),
            details: (
              <TableActionsContainer>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, auto)', gap: 8 }}>
                  <PackageDetailsButton name={pkg.name} label="Package Details" />
                  <PackageVersionDetailsButton name={pkg.name} version={pkg.version} label="Version Details" />
                </div>
              </TableActionsContainer>
            ),
          }))}
        />
      </div>
    </div>
  )
}

export default BaselinePackagesTable
